// packages/core/src/learning/school-modules/anti-scam.ts
// ─────────────────────────────────────────────────────────────────────────
// LENS PACK — "Anti-Scam" (surface: 'school').
//
// Conforms to LearningModule (LearningTypes.ts) and passes _validateLensPacks:
//   - surface: 'school', homeSchool: 'anti-scam', no assessment
//   - every lesson lessonKind: 'school-core', exactly 3 role-tagged flashcards
//   - exactly one applyAction per lesson, all live + allowed on 'lens-lesson'
//     (all three use noToolAction — reflection prompts)
//   - lesson ids namespaced `as-*`, flashcard ids `as-*-fc-*` (globally unique)
//   - concepts non-empty, no intra-pack duplicates
//
// Module id / homeSchool match the locked naming convention so the school's
// exhibit nativeModuleId: 'school-anti-scam' resolves.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_ANTI_SCAM_MODULE: LearningModule = {
	id: 'school-anti-scam',
	title: 'Anti-Scam',
	description:
		'The worldview that scams beat judgment by speeding it up. Learn to read the pressure before the technical trick, verify through a separate door, and protect yourself with calm rules made before the emergency.',
	emoji: '🛡️',
	accentColor: '#c8ff00',
	tier: 'stability',
	pillar: 'stability-protection',
	order: 1,
	surface: 'school',
	homeSchool: 'anti-scam',
	lessons: [
		// ── Lesson 1 ─────────────────────────────────────────────────────
		{
			id: 'as-pressure-not-stupidity',
			title: 'Scams exploit pressure, not stupidity',
			description:
				'This lesson teaches why the anti-scam lens focuses on pressure, urgency, and emotion before it focuses on technical details.',
			pillar: 'stability-protection',
			schoolsOfThought: ['anti-scam'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 4,
			difficulty: 1,
			tags: ['scams', 'manipulation', 'pause-rule'],
			prerequisites: [],
			relatedLessons: ['as-separate-door', 'as-calm-not-paranoia'],
			lessonKind: 'school-core',
			concepts: [
				'pressure reveals risk',
				'emotion before logic',
				'scam shame trap',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Scams rarely begin with someone asking you to make a calm decision. They begin with pressure, fear, greed, romance, urgency, authority, or shame.',
				},
				{
					type: 'paragraph',
					text: 'The anti-scam lens believes the first thing to study is not the scam itself. It is the emotional state the scam is trying to create in you.',
				},
				{
					type: 'bullet_list',
					items: [
						'Urgency makes you skip verification',
						'Fear makes you obey authority',
						'Excitement makes risk feel smaller',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking only careless people get scammed. Smart people get scammed when their emotions are forced to move faster than their judgment.',
				},
				{
					type: 'example',
					text: 'Someone texts you pretending to be your bank. They say your account is locked and you must click a link now. The scam is not just the fake link. The scam is the panic that makes the fake link feel reasonable.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can make you more suspicious than usual. That is useful when money, identity, passwords, or personal documents are involved, but it can become exhausting if every normal mistake feels like a trap.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, the action is to create a personal pause rule. When a money request creates pressure, you do not solve it inside the pressure. You step out, verify through a separate channel, and decide when your nervous system is calmer.',
				},
				{
					type: 'key_takeaway',
					text: 'Scams win when pressure outruns judgment.',
				},
			],
			audioScript:
				'The anti-scam worldview starts with one idea: scams are not mainly about stupidity. They are about pressure. That matters because a lot of people carry shame around being fooled, or they imagine scams only work on people who are careless, older, desperate, or not paying attention. That belief is dangerous, because it makes you underestimate how scams actually work.\n\nA good scam is not trying to beat your intelligence in a fair debate. It is trying to move you into a state where intelligence gets bypassed. Panic does that. Excitement does that. Romance does that. Authority does that. Shame does that. Scarcity does that. The scammer does not need you to become a different person. They only need you to act too fast for five minutes.\n\nThink about a fake bank alert. The text says your account is frozen. It gives you a link. It says you need to act now. The technical trick might be a fake website, but the real trick is emotional. You are not thinking, “Let me analyze this domain name carefully.” You are thinking, “I need to protect my money before something bad happens.” That urgency is the scam.\n\nOr take a fake job offer. They tell you that you were selected quickly, that the role pays well, and that they need some information to onboard you. If you have been stressed about money, that message does not land as random internet nonsense. It lands as relief. You want it to be real. That hope can make basic verification feel like getting in the way of your own opportunity.\n\nThe anti-scam lens says: do not judge yourself by whether you feel emotion. Everyone feels emotion. Judge the situation by whether someone is using that emotion to speed up your decision. If someone needs you to decide before you can verify, slow down. If someone tells you not to call the official number, slow down. If someone says secrecy is required, slow down. If someone tells you that asking questions proves you do not trust them, slow down.\n\nThe blind spot is that this lens can make you overly guarded. Not every urgent message is a scam. Not every mistake is manipulation. But when the situation involves money, identity, passwords, bank accounts, loans, crypto, taxes, romance, jobs, family emergencies, or legal threats, caution is not paranoia. It is protection.\n\nA simple rule works: never make a money decision inside the emotional state someone else created for you. Step away. Verify through a separate channel. Call the official number. Type the website yourself. Ask a trusted person. Sleep on it when possible. The scam wants speed. Your protection is time.',
			flashcards: [
				{
					id: 'as-pressure-not-stupidity-fc-main',
					role: 'main_idea',
					front: 'What do scams exploit first?',
					back: 'They usually exploit pressure, fear, excitement, trust, or shame before technical details.',
				},
				{
					id: 'as-pressure-not-stupidity-fc-watch',
					role: 'watch_out',
					front: 'What is the shame trap?',
					back: 'Believing only careless people get scammed, which makes you underestimate emotional manipulation.',
				},
				{
					id: 'as-pressure-not-stupidity-fc-use',
					role: 'use_it',
					front: 'What is a pause rule?',
					back: 'Do not decide inside pressure. Step away, verify separately, then decide calmly.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Build your scam pause rule',
				prompt: 'Write one rule you will follow whenever a message pressures you to move money, share information, or click quickly.',
			},
			revision: 1,
		},

		// ── Lesson 2 ─────────────────────────────────────────────────────
		{
			id: 'as-separate-door',
			title: 'Verify through a separate door',
			description:
				'This lesson teaches why the anti-scam lens treats independent verification as the difference between trust and blind trust.',
			pillar: 'stability-protection',
			schoolsOfThought: ['anti-scam'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['verification', 'separate-channel', 'fake-authority'],
			prerequisites: ['as-pressure-not-stupidity'],
			relatedLessons: [
				'as-pressure-not-stupidity',
				'as-calm-not-paranoia',
			],
			lessonKind: 'school-core',
			concepts: [
				'separate channel verification',
				'trust but check',
				'fake authority signals',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A scam often looks real because it borrows the costume of something you already trust: your bank, your boss, a delivery company, a government office, a platform, or someone you care about.',
				},
				{
					type: 'paragraph',
					text: 'The anti-scam lens believes trust is not the problem. Blind trust is the problem. The answer is not to trust nobody. The answer is to verify through a separate door.',
				},
				{
					type: 'bullet_list',
					items: [
						'Do not click the link they gave you',
						'Do not call the number they sent you',
						'Do not use the payment method they demand',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is verifying inside the scammer’s world. If the same message gives you the problem and the solution, step outside that message.',
				},
				{
					type: 'example',
					text: 'You get an email that looks like your payment app. It says your account needs review. Instead of clicking the email link, you open the app directly or type the official website yourself. That separate path breaks the scammer’s control.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that verification adds friction. It can feel annoying when the message is real. But friction is exactly what protects you when the message is fake.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a checklist: who contacted you, what emotion did they create, what action do they want, and how can you verify without using the path they provided?',
				},
				{
					type: 'key_takeaway',
					text: 'Real verification happens outside the channel that pressured you.',
				},
			],
			audioScript:
				'The anti-scam lens has a simple rule that protects you from a huge number of traps: verify through a separate door. That means you do not verify a message using the same path the message gives you. If a text says your bank account is locked and gives you a link, you do not click the link to check. If an email says your package needs a fee and gives you a payment page, you do not use that page to check. If someone calls saying they are from a company and gives you a callback number, you do not use that number as proof. You step outside the world they created.\n\nThis matters because scams often work by controlling the whole environment. They give you the fear, then they give you the solution. They create the emergency, then they offer the escape hatch. They pretend to be the trusted source, then they define what trust should look like. If you stay inside that channel, everything can look consistent because the scammer is writing the script.\n\nA fake email can use a real logo. A fake text can mention a real company. A fake caller can sound professional. A fake job recruiter can use normal hiring language. A fake romance scammer can remember personal details. None of those things prove the request is safe. They only prove the scammer knows how trust signals look.\n\nSeparate door verification means you choose your own path back to the source. Open the bank app yourself. Type the website yourself. Call the number printed on the back of your card. Contact your boss through the work channel you already use. Ask your family member a question only they would know. Check the official account, not the account that messaged you. The key is that you are not using the path provided by the pressure source.\n\nThis lens is not saying you should live paranoid. It is saying that trust should have a process when money, identity, access, or personal information is involved. Normal life needs trust. You cannot verify every tiny thing all day. But when someone asks you to send money, buy gift cards, move crypto, share a code, reset a password, scan an ID, pay a fee, accept a check, or keep something secret, the cost of checking is small compared with the cost of being wrong.\n\nThe blind spot is friction. Verification slows things down. Sometimes the message is real and the extra step feels unnecessary. But that friction is the protection. Scammers hate friction because friction gives your judgment time to come back online.\n\nA useful mental checklist is: who contacted me, what do they want, what feeling are they creating, what happens if I wait, and how can I verify through a path I chose myself? If you can answer those questions, you are no longer just reacting. You are back in control.',
			flashcards: [
				{
					id: 'as-separate-door-fc-main',
					role: 'main_idea',
					front: 'What is separate door verification?',
					back: 'Checking through a path you choose, not the link, number, or channel the message gave you.',
				},
				{
					id: 'as-separate-door-fc-watch',
					role: 'watch_out',
					front: 'What is fake authority?',
					back: 'A scam borrowing logos, titles, urgency, or official language to make obedience feel safe.',
				},
				{
					id: 'as-separate-door-fc-use',
					role: 'use_it',
					front: 'How do you verify a bank alert?',
					back: 'Open the app yourself or call the official number, instead of using the message link.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Make a verification checklist',
				prompt: 'Write your own four step checklist for verifying any message that asks for money, passwords, codes, documents, or secrecy.',
			},
			revision: 1,
		},

		// ── Lesson 3 ─────────────────────────────────────────────────────
		{
			id: 'as-calm-not-paranoia',
			title: 'Protection needs calm, not paranoia',
			description:
				'This lesson teaches where the anti-scam lens helps, where it can go too far, and how to protect yourself without becoming frozen.',
			pillar: 'stability-protection',
			schoolsOfThought: ['anti-scam'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['calm-protection', 'money-rules', 'fraud-resistance'],
			prerequisites: ['as-pressure-not-stupidity', 'as-separate-door'],
			relatedLessons: ['as-pressure-not-stupidity', 'as-separate-door'],
			lessonKind: 'school-core',
			concepts: [
				'calm protection',
				'suspicion tradeoff',
				'fraud resistant habits',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'The anti-scam lens is powerful because it teaches you to notice manipulation early. But if you take it too far, every message starts to feel dangerous and every opportunity starts to feel fake.',
				},
				{
					type: 'paragraph',
					text: 'This worldview works best when it creates calm systems, not constant fear. You want habits that protect you before panic shows up.',
				},
				{
					type: 'bullet_list',
					items: [
						'Keep money movement rules simple',
						'Keep passwords and codes private',
						'Keep trusted contacts for second opinions',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking protection means feeling suspicious all the time. Better protection means having rules ready before the pressure starts.',
				},
				{
					type: 'example',
					text: 'Instead of deciding from scratch every time someone asks for money, you have a rule: no gift cards, no secrecy, no payment from pressure, no codes over text, and no rushed transfers.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that anti-scam thinking can sometimes make you slower to trust real people or real opportunities. That is why the goal is not fear. The goal is a repeatable process.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your wider money life. Your runway, accounts, passwords, documents, and family communication habits all shape how vulnerable you are when someone tries to rush you.',
				},
				{
					type: 'key_takeaway',
					text: 'The strongest scam protection is a calm rule made before the emergency.',
				},
			],
			audioScript:
				'The anti-scam lens can protect your money, your identity, your time, and your future options. But it has a blind spot. If you use it without balance, it can turn into constant suspicion. You start reading every message like a trap. You start assuming every opportunity is fake. You start feeling like the only safe move is to trust nobody. That is not the goal.\n\nThe strongest version of this lens is calm. It does not make you panic more. It gives you rules so you can panic less. A scammer wants you improvising under pressure. You want a process that already exists before the pressure arrives.\n\nThink about how much easier this becomes when your rules are already written. No gift cards as payment. No sharing login codes. No moving money because someone says it is urgent. No keeping money requests secret. No clicking financial links from texts. No accepting checks from strangers and sending money back. No sending crypto to someone you have never met. No paying fees to receive a prize. No romantic partner needing emergency money before basic trust has been built. These rules are not perfect, but they remove a lot of decision making from the worst possible moment.\n\nThis matters because scams often attack the parts of life where people already feel stress. If you are behind on bills, a fake loan offer feels like rescue. If you are lonely, a romance scam feels like connection. If you are looking for work, a fake job feels like relief. If your family is under pressure, an emergency message feels believable. The anti-scam lens is not judging those emotions. It is saying those emotions deserve protection.\n\nCalm protection also means setting up your environment. Use strong unique passwords. Use two factor authentication when available. Keep recovery codes safe. Know where your documents are. Know who you would call for a second opinion. Talk with family members about emergency money requests before one happens. If someone claims to be a relative in trouble, have a way to verify. If someone claims to be a company, go directly to that company through your own path.\n\nThe tradeoff is that rules can sometimes slow down real situations. A real person might need help. A real company might contact you. A real opportunity might have a deadline. That is why the lens should not become automatic rejection. It should become automatic verification. You are not saying no to everything. You are saying, “I do not move money or share access until the situation survives a calm check.”\n\nThat is the mature version of anti-scam thinking. It protects without making fear your personality. It keeps you open to real life while making you harder to rush, flatter, threaten, or manipulate. The goal is not to become impossible to fool. The goal is to become harder to pressure.',
			flashcards: [
				{
					id: 'as-calm-not-paranoia-fc-main',
					role: 'main_idea',
					front: 'What is calm protection?',
					back: 'Having simple rules ready before pressure, fear, or urgency shows up.',
				},
				{
					id: 'as-calm-not-paranoia-fc-watch',
					role: 'watch_out',
					front: 'What is the paranoia trap?',
					back: 'Letting protection become constant suspicion instead of a repeatable verification process.',
				},
				{
					id: 'as-calm-not-paranoia-fc-use',
					role: 'use_it',
					front: 'What rule blocks many scams?',
					back: 'Never move money, share codes, or keep secrets because a message creates urgency.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Write your money movement rules',
				prompt: 'Write five rules for when you will not move money, share codes, click links, or send documents.',
			},
			revision: 1,
		},
	],
};
